/**
 * Ad blocker detection utility for PDF.js compatibility
 *
 * Ad blockers can interfere with PDF.js rendering by blocking:
 * - Canvas rendering operations
 * - Worker scripts
 * - Resource loading
 * - DOM manipulation
 */

export interface AdBlockerDetectionResult {
  isBlocked: boolean
  method: string
  confidence: 'high' | 'medium' | 'low'
}

/**
 * Detects ad blockers using multiple methods for better accuracy
 */
export const detectAdBlocker = async (): Promise<AdBlockerDetectionResult> => {
  const results: AdBlockerDetectionResult[] = []

  // Method 1: Test element blocking (most reliable)
  results.push(await testElementBlocking())

  // Method 2: Test script loading
  results.push(await testScriptBlocking())

  // Method 3: Test canvas manipulation (PDF.js specific)
  results.push(testCanvasBlocking())

  // Return the result with highest confidence that indicates blocking
  const blockedResults = results.filter((r) => r.isBlocked)
  if (blockedResults.length > 0) {
    return blockedResults.reduce((prev, current) =>
      getConfidenceScore(current.confidence) >
      getConfidenceScore(prev.confidence)
        ? current
        : prev
    )
  }

  return { isBlocked: false, method: 'none', confidence: 'high' }
}

/**
 * Test if ad-like elements are being blocked
 */
const testElementBlocking = (): Promise<AdBlockerDetectionResult> => {
  return new Promise((resolve) => {
    const testElement = document.createElement('div')
    testElement.className = 'adsbygoogle ads advertisement'
    // UI-13: the bait must NOT hide itself.
    //
    // This used to set `visibility: hidden !important` here and then read
    // `getComputedStyle(...).visibility === 'hidden'` below as proof of a
    // blocker -- so the probe detected its own styling. It returned
    // `isBlocked: true` at `confidence: 'high'` for EVERY visitor, which is
    // why people with no ad blocker were told they had one.
    //
    // It is moved off-screen instead: rendered and measurable, but never seen.
    // Anything that then hides or collapses it was done by something else,
    // which is the only signal worth reading.
    testElement.style.cssText = `
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
      width: 1px !important;
      height: 1px !important;
    `

    document.body.appendChild(testElement)

    setTimeout(() => {
      const style = window.getComputedStyle(testElement)
      const isBlocked =
        // Removed outright by an extension.
        !testElement.isConnected ||
        testElement.offsetHeight === 0 ||
        testElement.offsetWidth === 0 ||
        style.display === 'none' ||
        style.visibility === 'hidden'

      testElement.remove()

      resolve({
        isBlocked,
        method: 'element-blocking',
        confidence: 'high',
      })
    }, 100)
  })
}

/**
 * Test if external scripts are being blocked
 */
const testScriptBlocking = (): Promise<AdBlockerDetectionResult> => {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = 'data:text/javascript;base64,Ly8gVGVzdCBzY3JpcHQ='
    script.style.display = 'none'

    let resolved = false

    script.onload = () => {
      if (!resolved) {
        resolved = true
        document.head.removeChild(script)
        resolve({
          isBlocked: false,
          method: 'script-loading',
          confidence: 'medium',
        })
      }
    }

    script.onerror = () => {
      if (!resolved) {
        resolved = true
        document.head.removeChild(script)
        resolve({
          isBlocked: true,
          method: 'script-loading',
          confidence: 'medium',
        })
      }
    }

    document.head.appendChild(script)

    // Timeout fallback
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        try {
          document.head.removeChild(script)
        } catch {
          // Element might already be removed
        }
        // UI-13: a timeout means we do not KNOW -- a slow machine, a cold
        // cache or a strict CSP on `data:` scripts all land here. Calling that
        // "blocked" turns not-knowing into an accusation, so it reports clear.
        resolve({
          isBlocked: false,
          method: 'script-loading-timeout',
          confidence: 'low',
        })
      }
    }, 1000)
  })
}

/**
 * Test canvas operations that PDF.js relies on
 */
const testCanvasBlocking = (): AdBlockerDetectionResult => {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1

    const context = canvas.getContext('2d')
    if (!context) {
      return {
        isBlocked: true,
        method: 'canvas-context',
        confidence: 'high',
      }
    }

    // Test basic canvas operations that PDF.js uses
    context.fillStyle = '#000000'
    context.fillRect(0, 0, 1, 1)

    const imageData = context.getImageData(0, 0, 1, 1)
    if (!imageData || imageData.data.length === 0) {
      return {
        isBlocked: true,
        method: 'canvas-operations',
        confidence: 'medium',
      }
    }

    return {
      isBlocked: false,
      method: 'canvas-operations',
      confidence: 'medium',
    }
  } catch {
    return {
      isBlocked: true,
      method: 'canvas-operations',
      confidence: 'high',
    }
  }
}

/**
 * Convert confidence level to numeric score for comparison
 */
const getConfidenceScore = (confidence: 'high' | 'medium' | 'low'): number => {
  switch (confidence) {
    case 'high':
      return 3
    case 'medium':
      return 2
    case 'low':
      return 1
    default:
      return 0
  }
}

/**
 * Check if the current environment might have PDF.js compatibility issues
 */
/**
 * What went wrong, as a CODE rather than a sentence.
 *
 * UI-13: this used to return English prose (`'Canvas 2D context not available'`
 * and friends), which the component then rendered directly and -- worse --
 * grepped for the substring "ad blocker" to choose its headline. Prose from a
 * non-React utility can never be translated: `detectAdBlocker` has no `t()`.
 * The words now live in `common:ad_blocker.*` and the utility reports facts.
 */
export type CompatIssueCode = 'ad_blocker' | 'no_canvas' | 'no_workers'

export interface CompatIssue {
  code: CompatIssueCode
  /** Which probe fired, for `ad_blocker` only. Interpolated into the label. */
  method?: string
}

export const checkPdfJsCompatibility = async (): Promise<{
  compatible: boolean
  /**
   * Whether an ad blocker specifically was detected.
   *
   * Callers used to infer this by substring-matching the English text in
   * `issues`. That is brittle, and wrong the moment those strings are
   * translated -- the app ships five locales.
   */
  adBlockerDetected: boolean
  issues: CompatIssue[]
}> => {
  const issues: CompatIssue[] = []

  // Check ad blocker
  const adBlockerResult = await detectAdBlocker()
  if (adBlockerResult.isBlocked) {
    issues.push({ code: 'ad_blocker', method: adBlockerResult.method })
  }

  // Check canvas support
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (!context) {
    issues.push({ code: 'no_canvas' })
  }

  // Check Worker support (PDF.js uses workers)
  if (typeof Worker === 'undefined') {
    issues.push({ code: 'no_workers' })
  }

  return {
    compatible: issues.length === 0,
    adBlockerDetected: adBlockerResult.isBlocked,
    issues,
  }
}
